import { useMemo, useState } from 'react';
import {
  Badge,
  Caption1,
  Input,
  Link,
  MessageBar,
  MessageBarBody,
  Spinner,
  Table,
  TableBody,
  TableCell,
  TableHeader,
  TableHeaderCell,
  TableRow,
  Text,
  tokens,
} from '@fluentui/react-components';
import { DismissRegular, SearchRegular } from '@fluentui/react-icons';
import { PageHeader } from '../components/PageHeader';
import { useApi } from '../api/useApi';
import { useAsync } from '../api/useAsync';
import { formatBytes } from '../util/bytes';
import type { OneDriveAccountDto } from '../api/types';

type SortKey = 'owner' | 'used' | 'percent' | 'files' | 'last';

function Kpi({ label, value, tone }: { label: string; value: string; tone?: string }) {
  return (
    <div style={{ background: tokens.colorNeutralBackground2, borderRadius: tokens.borderRadiusMedium, padding: '12px 16px', minWidth: 140 }}>
      <div style={{ fontSize: 12, color: tokens.colorNeutralForeground3 }}>{label}</div>
      <div style={{ fontSize: 22, fontWeight: 600, color: tone }}>{value}</div>
    </div>
  );
}

export function OneDriveOverviewPage() {
  const api = useApi();
  const { data, loading, error } = useAsync(() => api.getOneDriveOverview(), [api]);
  const [search, setSearch] = useState('');
  const [sort, setSort] = useState<{ key: SortKey; dir: 1 | -1 }>({ key: 'used', dir: -1 });

  const rows = useMemo(() => {
    let r = data?.accounts ?? [];
    const q = search.trim().toLowerCase();
    if (q) r = r.filter((a) => a.ownerName.toLowerCase().includes(q) || a.ownerUpn.toLowerCase().includes(q));
    const val = (a: OneDriveAccountDto): number | string => {
      switch (sort.key) {
        case 'owner': return a.ownerName.toLowerCase();
        case 'percent': return a.percentUsed;
        case 'files': return a.fileCount;
        case 'last': return a.lastActivity ? new Date(a.lastActivity).getTime() : 0;
        default: return a.storageUsedBytes;
      }
    };
    return [...r].sort((x, y) => {
      const a = val(x), b = val(y);
      return (a < b ? -1 : a > b ? 1 : 0) * sort.dir;
    });
  }, [data, search, sort]);

  function head(key: SortKey, label: string) {
    const active = sort.key === key;
    return (
      <TableHeaderCell
        style={{ cursor: 'pointer' }}
        onClick={() => setSort((s) => ({ key, dir: s.key === key ? (s.dir === 1 ? -1 : 1) : -1 }))}
      >
        {label}{active ? (sort.dir === 1 ? ' ↑' : ' ↓') : ''}
      </TableHeaderCell>
    );
  }

  return (
    <div>
      <PageHeader title="OneDrive-Übersicht" description="Speicher aller OneDrives im Tenant — als Ersatz für den SharePoint-Admin-Bericht." />

      {loading ? <Spinner label="Lade Bericht…" /> : null}
      {error ? <MessageBar intent="error"><MessageBarBody>{error.message}</MessageBarBody></MessageBar> : null}

      {data ? (
        <>
          <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', marginBottom: tokens.spacingVerticalL }}>
            <Kpi label="Gesamt genutzt" value={formatBytes(data.totalUsedBytes)} />
            <Kpi label="Gesamt zugewiesen" value={formatBytes(data.totalAllocatedBytes)} />
            <Kpi label="OneDrives" value={data.count.toLocaleString('de-DE')} />
            <Kpi label="Nahe Quota (≥85%)" value={String(data.nearQuotaCount)} tone={data.nearQuotaCount > 0 ? tokens.colorPaletteRedForeground1 : undefined} />
            <Kpi label="Inaktiv (>90 T)" value={String(data.inactiveCount)} tone={data.inactiveCount > 0 ? tokens.colorPaletteYellowForeground1 : undefined} />
          </div>

          <div style={{ maxWidth: 360, marginBottom: tokens.spacingVerticalM }}>
            <Input
              placeholder="Nach Name oder UPN suchen…"
              value={search}
              onChange={(_, d) => setSearch(d.value)}
              contentBefore={<SearchRegular />}
              contentAfter={search ? <DismissRegular style={{ cursor: 'pointer' }} onClick={() => setSearch('')} aria-label="Suche leeren" /> : undefined}
            />
          </div>

          {rows.length === 0 ? (
            <Text>Keine OneDrives gefunden. Falls der Bericht leer ist, sind die Namen im Tenant evtl. anonymisiert.</Text>
          ) : (
            <Table size="small" aria-label="OneDrive-Übersicht">
              <TableHeader>
                <TableRow>
                  {head('owner', 'Besitzer')}
                  {head('used', 'Genutzt')}
                  <TableHeaderCell>Quota</TableHeaderCell>
                  {head('percent', 'Auslastung')}
                  {head('files', 'Dateien')}
                  {head('last', 'Letzte Aktivität')}
                  <TableHeaderCell>Site</TableHeaderCell>
                </TableRow>
              </TableHeader>
              <TableBody>
                {rows.map((a) => (
                  <TableRow key={a.ownerUpn || a.siteUrl}>
                    <TableCell>
                      <div style={{ display: 'flex', flexDirection: 'column' }}>
                        <Text size={200} weight="semibold" className="pii">{a.ownerName || '—'}</Text>
                        <Caption1 className="pii" style={{ color: tokens.colorNeutralForeground3 }}>{a.ownerUpn}</Caption1>
                      </div>
                    </TableCell>
                    <TableCell>{formatBytes(a.storageUsedBytes)}</TableCell>
                    <TableCell>{formatBytes(a.storageAllocatedBytes)}</TableCell>
                    <TableCell>
                      <Badge appearance="tint" color={a.percentUsed >= 85 ? 'danger' : a.percentUsed >= 60 ? 'warning' : 'success'}>
                        {a.percentUsed}%
                      </Badge>
                    </TableCell>
                    <TableCell>{a.fileCount.toLocaleString('de-DE')}</TableCell>
                    <TableCell>{a.lastActivity ? new Date(a.lastActivity).toLocaleDateString('de-DE') : '—'}</TableCell>
                    <TableCell>{a.siteUrl ? <Link href={a.siteUrl} target="_blank" rel="noreferrer">öffnen</Link> : '—'}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
          <Caption1 style={{ display: 'block', marginTop: 12, color: tokens.colorNeutralForeground3 }}>
            Quelle: Microsoft-365-Nutzungsbericht (Stand 1–2 Tage alt).
          </Caption1>
        </>
      ) : null}
    </div>
  );
}
